/**
 * Sales stream / business unit filter for Orders Transactions.
 *
 * 1. Selection limit (max 10 weighted items): a fully selected business unit
 *    counts as 1; each individually picked terminal counts as 1.
 * 2. API query: when every terminal of a unit is selected, send businessUnitId
 *    instead of listing all salesStream IDs (avoids 422 on long salesStream arrays).
 */

/** Max weighted selection count enforced in the terminal filter UI. */
export const MAX_TERMINAL_FILTER_SELECTION = 10

function toIdSet(ids) {
  return new Set(ids.map(String))
}

/** Route query arrays parsed by qs may arrive as `{ 0: 'a', 1: 'b' }` objects. */
function isQsParsedArray(value) {
  const keys = Object.keys(value)
  return keys.length > 0 && keys.every((key) => /^\d+$/.test(key))
}

/** API responses use `salesStreams` (plural) or `salesStream` (singular). */
function getBusinessUnitStreams(businessUnit) {
  return businessUnit?.salesStreams ?? businessUnit?.salesStream ?? []
}

/**
 * Maps the current selection onto each business unit.
 * Only units with at least one selected terminal are returned.
 */
function getUnitSelections(selected, businessUnits) {
  const selectedSet = toIdSet(selected)

  return businessUnits
    .map((businessUnit) => {
      const streamIds = getTerminalStreamIds(businessUnit)
      const selectedInUnit = streamIds.filter((id) =>
        selectedSet.has(String(id))
      )

      return { businessUnit, streamIds, selectedInUnit }
    })
    .filter(({ selectedInUnit }) => selectedInUnit.length > 0)
}

/** Stream IDs selected in the UI but not belonging to any loaded business unit. */
function getOrphanSelectedIds(selected, unitSelections) {
  const knownStreamIds = new Set(
    unitSelections.flatMap(({ streamIds }) => streamIds.map(String))
  )

  return selected.filter((id) => !knownStreamIds.has(String(id)))
}

/** Full branch = 1 item; partial branch = one item per selected terminal. */
function weightForUnitSelection({ streamIds, selectedInUnit }) {
  return selectedInUnit.length === streamIds.length ? 1 : selectedInUnit.length
}

/** API accepts a single string or an array when multiple units are fully selected. */
function buildBusinessUnitIdParam(businessUnitIds) {
  if (businessUnitIds.length === 1) {
    return { businessUnitId: businessUnitIds[0] }
  }

  if (businessUnitIds.length > 1) {
    return { businessUnitId: businessUnitIds }
  }

  return {}
}

/**
 * Normalizes salesStream filter values from route query / filter state.
 * Accepts arrays, single strings, numbers, or qs-parsed array objects.
 */
export function normalizeSalesStreamValues(value) {
  if (!value) {
    return []
  }

  if (Array.isArray(value)) {
    return value.filter(Boolean).map(String)
  }

  if (typeof value === 'string' && value.length > 0) {
    return [value]
  }

  if (typeof value === 'number') {
    return [String(value)]
  }

  if (typeof value === 'object' && isQsParsedArray(value)) {
    return Object.keys(value)
      .sort((a, b) => Number(a) - Number(b))
      .map((key) => value[key])
      .filter(Boolean)
      .map(String)
  }

  return []
}

/** Physical terminals only — ECOM / webshop streams have `terminalId: null`. */
export function getTerminalStreams(businessUnit) {
  return getBusinessUnitStreams(businessUnit).filter(
    (stream) => stream.terminalId !== null
  )
}

export function getTerminalStreamIds(businessUnit) {
  return getTerminalStreams(businessUnit)
    .map((stream) => stream.id)
    .filter(Boolean)
}

/**
 * Returns the weighted selection count used to enforce the UI limit.
 * Example: 1 full unit (5 terminals) + 3 terminals elsewhere = 4 items.
 */
export function calculateSelectionWeight(
  selectedStreamIds,
  businessUnits = []
) {
  const selected = normalizeSalesStreamValues(selectedStreamIds)
  if (!selected.length) {
    return 0
  }

  const unitSelections = getUnitSelections(selected, businessUnits)
  const unitWeight = unitSelections.reduce(
    (total, selection) => total + weightForUnitSelection(selection),
    0
  )
  const orphanWeight = getOrphanSelectedIds(selected, unitSelections).length

  return unitWeight + orphanWeight
}

export function wouldExceedSelectionLimit(
  selectedStreamIds,
  businessUnits = [],
  maxWeight = MAX_TERMINAL_FILTER_SELECTION
) {
  return calculateSelectionWeight(selectedStreamIds, businessUnits) > maxWeight
}

export function isSelectionLimitReached(
  selectedStreamIds,
  businessUnits = [],
  maxWeight = MAX_TERMINAL_FILTER_SELECTION
) {
  return calculateSelectionWeight(selectedStreamIds, businessUnits) >= maxWeight
}

/**
 * Converts route-stored salesStream IDs into API query params.
 * Full unit selections become businessUnitId; the rest stay as salesStream.
 */
export function optimizeSalesStreamQuery(
  selectedStreamIds,
  businessUnits = []
) {
  const selected = normalizeSalesStreamValues(selectedStreamIds)
  if (!selected.length) {
    return {}
  }

  // Business units not loaded yet — keep raw IDs so the filter is not dropped.
  if (!businessUnits.length) {
    return { salesStream: selected }
  }

  const unitSelections = getUnitSelections(selected, businessUnits)
  const businessUnitIds = []
  const salesStream = []

  unitSelections.forEach(({ businessUnit, streamIds, selectedInUnit }) => {
    const isFullySelected = selectedInUnit.length === streamIds.length

    if (isFullySelected && businessUnit.id) {
      businessUnitIds.push(businessUnit.id)
      return
    }

    salesStream.push(...selectedInUnit)
  })

  salesStream.push(...getOrphanSelectedIds(selected, unitSelections))

  return {
    ...buildBusinessUnitIdParam(businessUnitIds),
    ...(salesStream.length ? { salesStream } : {})
  }
}

/**
 * Whether the location-scoped businessUnitId should be added to the API query.
 * Skip when a partial salesStream filter is active — terminal IDs already narrow
 * the result set and businessUnitId would incorrectly imply all terminals.
 */
export function shouldApplyLocationBusinessUnitId(query = {}) {
  const salesStream = normalizeSalesStreamValues(query.salesStream)

  return salesStream.length === 0
}
